import { useState, useEffect } from 'react'
import { useApi } from '@/hooks/useApi'

interface UserStats {
  total: number
  by_role: Record<string, number>
  new_today: number
  new_week: number
}

interface ProxyStats {
  total: number
  active: number
  inactive: number
  by_country: Record<string, number>
  by_type: Record<string, number>
}

export default function AdminDashboard() {
  const { api } = useApi()
  const [userStats, setUserStats] = useState<UserStats | null>(null)
  const [proxyStats, setProxyStats] = useState<ProxyStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  useEffect(() => {
    Promise.allSettled([
      api.get<UserStats>('/auth/stats').then(r => r.data).catch(() => null),
      api.get<ProxyStats>('/proxies/stats').then(r => r.data).catch(() => null),
    ]).then(([us, ps]) => {
      setUserStats(us?.status === 'fulfilled' ? (us.value ?? null) : null)
      setProxyStats(ps?.status === 'fulfilled' ? (ps.value ?? null) : null)
      setLoading(false)
    }).catch(err => {
      setError(err.response?.data?.detail || 'Failed to load stats')
      setLoading(false)
    })
  }, [])
  
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    )
  }
  
  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Users */}
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500 mb-1">Total Users</p>
          <p className="text-3xl font-bold text-gray-900">{userStats?.total || 0}</p>
          <p className="text-xs text-green-600 mt-2">
            +{userStats?.new_today || 0} today
          </p>
        </div>
        
        {/* Active Proxies */}
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500 mb-1">Active Proxies</p>
          <p className="text-3xl font-bold text-gray-900">{proxyStats?.active || 0}</p>
          <p className="text-xs text-gray-500 mt-2">
            of {proxyStats?.total || 0} total
          </p>
        </div>
        
        {/* Countries */}
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500 mb-1">Countries</p>
          <p className="text-3xl font-bold text-gray-900">
            {Object.keys(proxyStats?.by_country || {}).length}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            proxy locations
          </p>
        </div>
        
        {/* Proxy Sources */}
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500 mb-1">Proxy Sources</p>
          <p className="text-3xl font-bold text-gray-900">
            {Object.keys(userStats?.by_role || {}).length}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            role types
          </p>
        </div>
      </div>
      
      {/* Detailed Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users by Role */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Users by Role</h2>
          <div className="space-y-3">
            {(Object.entries(userStats?.by_role || {}) as [string, number][]).map(([role, count]) => (
              <div key={role} className="flex items-center justify-between">
                <span className="capitalize text-gray-700">{role}</span>
                <span className="font-medium text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Proxies by Country */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Proxies by Country</h2>
          <div className="space-y-3">
            {Object.entries(proxyStats?.by_country || {})
              .sort(([, a], [, b]) => b - a)
              .slice(0, 10)
              .map(([country, count]) => (
                <div key={country} className="flex items-center justify-between">
                  <span className="text-gray-700">{country}</span>
                  <span className="font-medium text-gray-900">{count}</span>
                </div>
              ))}
          </div>
        </div>
        
        {/* Proxies by Type */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Proxies by Type</h2>
          <div className="space-y-3">
            {Object.entries(proxyStats?.by_type || {})
              .sort(([, a], [, b]) => b - a)
              .map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-gray-700">{type}</span>
                  <span className="font-medium text-gray-900">{count}</span>
                </div>
              ))}
          </div>
        </div>
        
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-2 text-sm text-gray-600">
            <p>New user registrations: {userStats?.new_today || 0} today</p>
            <p>Weekly growth: {userStats?.new_week || 0} users</p>
            <p>Proxy health: {proxyStats ? `${proxyStats.active}/${proxyStats.total} active` : '—'}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
